import React from 'react';
import { matchRoutePattern } from './match-route';
import { recordRumMilestone, RumMilestone } from '@/services/rum';

type PrefetchEntry = {
  pattern: string;
  importFn: () => Promise<{ default: React.ComponentType }>;
};

const prefetchRegistry: PrefetchEntry[] = [];

/**
 * Drop-in replacement for `React.lazy` that also registers the route for
 * hover-based prefetching. When a user hovers over an `<a>` whose `href`
 * matches `pattern`, the chunk is fetched in the background so navigation
 * is instant.
 *
 * Once the module resolves, subsequent renders use the resolved component
 * synchronously — completely bypassing React.lazy and Suspense.
 */
// oxlint-disable-next-line typescript/no-explicit-any -- React.ComponentType<unknown> is too strict for components with optional props
export function lazyRoute<T extends React.ComponentType<any>>(
  pattern: string,
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  let resolved: T | null = null;
  let pending: Promise<{ default: T }> | null = null;

  const load = () => {
    pending ??= factory()
      .then((m) => {
        resolved = m.default;
        return m;
      })
      .catch((error) => {
        pending = null;
        throw error;
      });
    return pending;
  };

  prefetchRegistry.push({ pattern, importFn: load });

  const Lazy = React.lazy(() => load());

  // Wrapper that renders the resolved component directly when available,
  // skipping React.lazy (and its microtask-delayed Suspense) entirely.
  function PrefetchedRoute(props: React.ComponentProps<T>) {
    // The only milestone that contains any of the page's own cost, and the
    // reason it lives here: while the page is suspended this subtree is not
    // committed, so the effect first runs on the render that actually shows
    // the page — after its chunk has been fetched, evaluated and rendered.
    // Every milestone before this one is complete while the Suspense fallback
    // is still on screen (FPS-3854). Recording is idempotent, so StrictMode's
    // double invoke and any later re-render keep the first timestamp.
    React.useEffect(() => {
      recordRumMilestone(RumMilestone.RoutePageRendered);
    }, []);

    if (resolved != null) {
      return React.createElement(resolved, props);
    }
    return React.createElement(Lazy, props);
  }

  // Cast so it's assignable everywhere React.lazy is used today.
  // The runtime type is a plain function component, which React handles fine.
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return PrefetchedRoute as unknown as React.LazyExoticComponent<T>;
}

const prefetched = new Set<string>();

function prefetchForPath(pathname: string) {
  if (prefetched.has(pathname)) return;
  prefetched.add(pathname);

  for (const { pattern, importFn } of prefetchRegistry) {
    if (matchRoutePattern(pathname, pattern)) {
      void importFn().catch(() => {
        prefetched.delete(pathname);
      });
    }
  }
}

/** Return the internal pathname from an anchor element, or null if not an internal link. */
export function getInternalPathname(anchor: HTMLAnchorElement): string | null {
  const href = anchor.getAttribute('href');
  if (href == null || !href.startsWith('/')) return null;
  return href.split(/[#?]/)[0];
}

/** Prefetch the route chunk for whichever link the user is hovering. */
function handleLinkHover(event_: MouseEvent) {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const anchor = (event_.target as Element).closest?.('a');
  if (anchor == null) return;

  const pathname = getInternalPathname(anchor);
  if (pathname != null) {
    prefetchForPath(pathname);
  }
}

let installed = false;

/**
 * Install a global listener that prefetches lazy route chunks when the user
 * hovers over a link. Call once at app startup.
 */
export function installLinkPrefetching() {
  if (installed) return;
  installed = true;

  // pointerenter doesn't bubble, so capture: true lets us delegate from document.
  // Unlike mouseover it only fires once per element entry, avoiding redundant work
  // from child elements inside anchors.
  document.addEventListener('pointerenter', handleLinkHover, { capture: true, passive: true });
}

/** Reset all module state. Test-only — not for production use. */
export function resetPrefetchState() {
  prefetchRegistry.length = 0;
  prefetched.clear();
  if (installed) {
    document.removeEventListener('pointerenter', handleLinkHover, { capture: true });
  }
  installed = false;
}
